import React, { useState, useRef, useEffect } from 'react';

// Image component with retry logic - falls back to fallbackSrc if provided, otherwise keeps retrying
const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  fallbackSrc = null,  // Optional fallback (e.g., original image if thumbnail fails)
  onClick,
  loading = 'lazy'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [usedFallback, setUsedFallback] = useState(false);
  const imgRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);

  // Reset state when src changes
  useEffect(() => {
    mountedRef.current = true;
    retryCountRef.current = 0;
    setIsLoading(true);
    setCurrentSrc(src);
    setUsedFallback(false);
    
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    return () => {
      mountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [src]);

  const handleLoad = () => {
    if (mountedRef.current) {
      setIsLoading(false);
    }
  };

  const handleError = () => {
    if (!mountedRef.current) return;
    
    // If we haven't tried fallback yet and one exists, use it
    if (!usedFallback && fallbackSrc && fallbackSrc !== src) {
      setUsedFallback(true);
      retryCountRef.current = 0;
      setCurrentSrc(fallbackSrc);
      return;
    }
    
    // Retry up to 3 times with increasing delays
    const currentUrl = usedFallback ? fallbackSrc : src;
    if (retryCountRef.current < 3 && currentUrl) {
      retryCountRef.current += 1;
      const delay = 500 * retryCountRef.current;
      
      retryTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current && imgRef.current) {
          const newSrc = currentUrl.includes('?') 
            ? `${currentUrl}&_retry=${retryCountRef.current}&_t=${Date.now()}`
            : `${currentUrl}?_retry=${retryCountRef.current}&_t=${Date.now()}`;
          setCurrentSrc(newSrc);
        }
      }, delay);
    }
    // After all retries, keep showing loading state (no stock photo fallback)
  };

  // If no src provided, show empty placeholder
  if (!src) {
    return (
      <div className={`relative bg-gray-100 ${className}`} onClick={onClick}>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-gray-400 text-sm">No image</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} onClick={onClick}>
      {/* Loading skeleton */}
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center z-10">
          <svg className="w-8 h-8 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}
      
      {/* Actual image */}
      <img
        ref={imgRef}
        src={currentSrc}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
};

// Thumbnail component - with fallback support
export const ThumbnailImage = ({ 
  src, 
  alt, 
  isSelected, 
  onClick,
  fallbackSrc = null  // Fallback to original image if thumbnail fails
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [usedFallback, setUsedFallback] = useState(false);
  const imgRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    retryCountRef.current = 0;
    setIsLoading(true);
    setCurrentSrc(src);
    setUsedFallback(false);
    
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    return () => {
      mountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [src]);

  const handleLoad = () => {
    if (mountedRef.current) {
      setIsLoading(false);
    }
  };

  const handleError = () => {
    if (!mountedRef.current) return;
    
    // If thumbnail fails and we have original image, use it
    if (!usedFallback && fallbackSrc && fallbackSrc !== src) {
      setUsedFallback(true);
      retryCountRef.current = 0;
      setCurrentSrc(fallbackSrc);
      return;
    }
    
    const currentUrl = usedFallback ? fallbackSrc : src;
    if (retryCountRef.current < 3 && currentUrl) {
      retryCountRef.current += 1;
      const delay = 500 * retryCountRef.current;
      
      retryTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current && imgRef.current) {
          const newSrc = currentUrl.includes('?') 
            ? `${currentUrl}&_retry=${retryCountRef.current}&_t=${Date.now()}`
            : `${currentUrl}?_retry=${retryCountRef.current}&_t=${Date.now()}`;
          setCurrentSrc(newSrc);
        }
      }, delay);
    }
  };

  if (!src) {
    return (
      <div 
        className={`h-20 w-20 rounded cursor-pointer border-2 transition-all overflow-hidden relative bg-gray-100 ${
          isSelected ? 'border-blue-600' : 'border-transparent hover:border-gray-300'
        }`}
        onClick={onClick}
      />
    );
  }

  return (
    <div 
      className={`h-20 w-20 rounded cursor-pointer border-2 transition-all overflow-hidden relative ${
        isSelected ? 'border-blue-600' : 'border-transparent hover:border-gray-300'
      }`}
      onClick={onClick}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse z-10" />
      )}
      
      <img
        ref={imgRef}
        src={currentSrc}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
};

export default OptimizedImage;
